import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Card, Badge, Button, DataTable, Select, Input, Textarea, Modal, ErrorState } from '../components/ui';
import { useApiQuery, useApiMutation, useInvalidateQueries } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { useOrdenesCargaEnabled } from '../hooks/useOrdenesCargaEnabled';
import { useFotoGuiaCaptura } from '../hooks/useFotoGuiaCaptura';
import ordenesCargaCombustibleService from '../services/ordenesCargaCombustibleService';
import FormularioOrdenCarga from '../components/ordenesCarga/FormularioOrdenCarga';
import FormularioSolicitudCarga from '../components/ordenesCarga/FormularioSolicitudCarga';
import FormularioEmitirOrden from '../components/ordenesCarga/FormularioEmitirOrden';
import type {
  EstadoOrdenCarga, EstadoDestino, OrdenCargaCombustible, OrdenCargaDestino, OrdenCargaCombustibleCreateRequest,
  OrdenCargaCombustibleSolicitarRequest, OrdenCargaCombustibleEmitirRequest, DestinoPorRevisar,
} from '../types/ordenesCargaCombustible';

type TabActiva = 'general' | 'mis-ordenes' | 'revisar';

type RechazarTarget =
  | { tipo: 'solicitud'; orden: OrdenCargaCombustible }
  | { tipo: 'destino'; orden: OrdenCargaCombustible; destino: OrdenCargaDestino };

const badgeVariantPorEstado: Record<EstadoOrdenCarga, 'gray' | 'yellow' | 'green' | 'red' | 'blue'> = {
  SOLICITADA: 'blue',
  AUTORIZADA: 'yellow',
  PENDIENTE: 'gray',
  APROBADA: 'green',
  RECHAZADA: 'red',
};

const badgeVariantPorEstadoDestino: Record<EstadoDestino, 'gray' | 'yellow' | 'green' | 'red' | 'blue'> = {
  PENDIENTE: 'gray',
  CARGADA: 'yellow',
  APROBADA: 'green',
  RECHAZADA: 'red',
};

const textoDestino = (d: OrdenCargaDestino) => d.vehiculo
  ? `🚚 ${d.vehiculo.patente}`
  : d.maquina
    ? `⚙️ ${d.maquina.codigo}`
    : '-';

const textoCantidad = (d: OrdenCargaDestino, unidad: string) =>
  d.cantidad_autorizada != null
    ? `${d.cantidad_autorizada} ${unidad}`
    : d.cantidad_solicitada != null
      ? `(${d.cantidad_solicitada} solicitados)`
      : '-';

// El badge de cada destino (en columnaDestinos) ya distingue PENDIENTE/CARGADA/
// APROBADA/RECHAZADA por línea. El estado del header solo aporta algo que esas
// líneas no muestran cuando la orden aún no se emitió (SOLICITADA) o cuando la
// empresa la rechazó completa antes de emitir (RECHAZADA con destinos todavía
// PENDIENTE) — en PENDIENTE/APROBADA emitidos es siempre redundante con las
// líneas, así que se oculta para no mostrar el mismo estado dos veces por fila.
const estadoHeaderEsRedundante = (orden: OrdenCargaCombustible) => {
  if (orden.estado === 'SOLICITADA' || orden.estado === 'AUTORIZADA') return false;
  if (orden.estado === 'RECHAZADA' && orden.destinos.every((d) => d.estado === 'PENDIENTE')) return false;
  return true;
};

const FotoGuiaDespachoThumb: React.FC<{ ordenId: number; destinoId: number }> = ({ ordenId, destinoId }) => {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelado = false;
    ordenesCargaCombustibleService.obtenerFotoGuiaDespachoUrl(ordenId, destinoId)
      .then((u) => {
        if (cancelado) {
          URL.revokeObjectURL(u);
          return;
        }
        objectUrl = u;
        setUrl(u);
      })
      .catch(() => {});
    return () => {
      cancelado = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [ordenId, destinoId]);

  if (!url) {
    return <div className="w-16 h-16 rounded border border-gray-200 bg-gray-50 animate-pulse" />;
  }
  return (
    <img
      src={url}
      alt="guía de despacho"
      className="w-16 h-16 object-cover rounded border border-gray-200"
    />
  );
};

/** Igual que FotoGuiaDespachoThumb, pero para la foto de horómetro+nivel
 * (opcional — el destino puede no tener una, ver ruta_foto_evidencia_nivel). */
const FotoEvidenciaNivelThumb: React.FC<{ ordenId: number; destinoId: number }> = ({ ordenId, destinoId }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelado = false;
    ordenesCargaCombustibleService.obtenerFotoEvidenciaNivelUrl(ordenId, destinoId)
      .then((u) => {
        if (cancelado) {
          URL.revokeObjectURL(u);
          return;
        }
        objectUrl = u;
        setUrl(u);
      })
      .catch(() => setError(true));
    return () => {
      cancelado = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [ordenId, destinoId]);

  if (error) return null;
  if (!url) {
    return <div className="w-16 h-16 rounded border border-gray-200 bg-gray-50 animate-pulse" />;
  }
  return (
    <img
      src={url}
      alt="horómetro y nivel"
      className="w-16 h-16 object-cover rounded border border-gray-200"
    />
  );
};

const OrdenesCargaCombustibleView: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { habilitado, cargando: cargandoConfig } = useOrdenesCargaEnabled();
  const { invalidate } = useInvalidateQueries();

  const canCrear = hasPermission('ordenes-carga.crear');
  const canAprobar = hasPermission('ordenes-carga.aprobar');
  const canEliminar = hasPermission('ordenes-carga.eliminar');

  const tabs: TabActiva[] = canAprobar ? ['general', 'mis-ordenes', 'revisar'] : ['general', 'mis-ordenes'];

  const [tabActiva, setTabActiva] = useState<TabActiva>('general');
  const [page, setPage] = useState(1);
  const [estadoFiltro, setEstadoFiltro] = useState<string>('');
  const [crearModalOpen, setCrearModalOpen] = useState(false);
  const [solicitarModalOpen, setSolicitarModalOpen] = useState(false);
  const [emitirOrden, setEmitirOrden] = useState<OrdenCargaCombustible | null>(null);
  const [cargarTarget, setCargarTarget] = useState<{ orden: OrdenCargaCombustible; destino: OrdenCargaDestino } | null>(null);
  const [rechazarTarget, setRechazarTarget] = useState<RechazarTarget | null>(null);
  const [detalleOrden, setDetalleOrden] = useState<OrdenCargaCombustible | null>(null);
  const [eliminarOrden, setEliminarOrden] = useState<OrdenCargaCombustible | null>(null);
  const [regularizarTarget, setRegularizarTarget] = useState<{ orden: OrdenCargaCombustible; destino: OrdenCargaDestino } | null>(null);
  const [cantidadCargada, setCantidadCargada] = useState<string>('');
  const [rutCargador, setRutCargador] = useState<string>('');
  const [nombreCargador, setNombreCargador] = useState<string>('');
  const [horometroDeclarado, setHorometroDeclarado] = useState<string>('');
  const [nivelDeclarado, setNivelDeclarado] = useState<string>('');
  const [regCantidad, setRegCantidad] = useState<string>('');
  const [regRut, setRegRut] = useState<string>('');
  const [regNombre, setRegNombre] = useState<string>('');
  const [regHorometro, setRegHorometro] = useState<string>('');
  const [regNivel, setRegNivel] = useState<string>('');
  const [observacionesRechazo, setObservacionesRechazo] = useState('');

  const fotoGuia = useFotoGuiaCaptura();
  const fotoNivel = useFotoGuiaCaptura();
  const fotoGuiaReg = useFotoGuiaCaptura();
  const fotoNivelReg = useFotoGuiaCaptura();

  const perPage = 20;

  const { data: dataGeneral, isLoading: loadingGeneral } = useApiQuery(
    ['ordenes-carga-general', page, estadoFiltro],
    () => ordenesCargaCombustibleService.listar({ page, per_page: perPage, estado: (estadoFiltro || undefined) as EstadoOrdenCarga | undefined }),
    { enabled: habilitado && tabActiva === 'general' }
  );

  const { data: dataMias, isLoading: loadingMias } = useApiQuery(
    ['ordenes-carga-mias', page],
    () => ordenesCargaCombustibleService.mias(undefined, page, perPage),
    { enabled: habilitado && tabActiva === 'mis-ordenes' }
  );

  const { data: dataRevisar, isLoading: loadingRevisar } = useApiQuery(
    ['ordenes-carga-revisar'],
    () => ordenesCargaCombustibleService.revisarCargas(),
    { enabled: habilitado && tabActiva === 'revisar' }
  );

  const invalidarTodo = () => {
    invalidate(['ordenes-carga-general']);
    invalidate(['ordenes-carga-mias']);
    invalidate(['ordenes-carga-revisar']);
  };

  const cerrarModalCargar = () => {
    setCargarTarget(null);
    setCantidadCargada('');
    setRutCargador('');
    setNombreCargador('');
    setHorometroDeclarado('');
    setNivelDeclarado('');
    fotoGuia.resetear();
    fotoNivel.resetear();
  };

  // Al abrir el modal de carga, prefill del RUT y nombre con los del operador
  // autorizado (editables): normalmente es quien carga.
  useEffect(() => {
    if (cargarTarget?.orden.operador?.rut) {
      setRutCargador((prev) => prev || cargarTarget.orden.operador!.rut || '');
    }
    const nombreOperador = cargarTarget?.orden.operador?.nombre_completo || cargarTarget?.orden.operador?.descripcion;
    if (nombreOperador) {
      setNombreCargador((prev) => prev || nombreOperador);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargarTarget]);

  const cerrarModalRechazar = () => {
    setRechazarTarget(null);
    setObservacionesRechazo('');
  };

  const abrirRegularizar = (orden: OrdenCargaCombustible, destino: OrdenCargaDestino) => {
    setRegularizarTarget({ orden, destino });
    setRegCantidad(destino.cantidad_cargada != null ? String(destino.cantidad_cargada) : '');
    setRegRut(destino.rut_cargador ?? '');
    setRegNombre(destino.nombre_cargador ?? '');
    setRegHorometro(destino.horometro_declarado != null ? String(destino.horometro_declarado) : '');
    setRegNivel(destino.nivel_declarado != null ? String(destino.nivel_declarado) : '');
  };

  const cerrarModalRegularizar = () => {
    setRegularizarTarget(null);
    setRegCantidad('');
    setRegRut('');
    setRegNombre('');
    setRegHorometro('');
    setRegNivel('');
    fotoGuiaReg.resetear();
    fotoNivelReg.resetear();
  };

  const handleDescargarPdf = async (orden: OrdenCargaCombustible) => {
    try {
      await ordenesCargaCombustibleService.descargarPdf(orden.id, orden.numero_orden);
    } catch (error: any) {
      toast.error(error?.message || t('ordenes_carga.messages.error_pdf'));
    }
  };

  const crearMutation = useApiMutation(
    (datos: OrdenCargaCombustibleCreateRequest) => ordenesCargaCombustibleService.crear(datos),
    {
      successMessage: t('ordenes_carga.messages.emitida'),
      onSuccess: () => { invalidarTodo(); setCrearModalOpen(false); },
    }
  );

  const solicitarMutation = useApiMutation(
    (datos: OrdenCargaCombustibleSolicitarRequest) => ordenesCargaCombustibleService.solicitar(datos),
    {
      successMessage: t('ordenes_carga.messages.solicitada'),
      onSuccess: () => { invalidarTodo(); setSolicitarModalOpen(false); },
    }
  );

  const autorizarMutation = useApiMutation(
    (id: number) => ordenesCargaCombustibleService.autorizar(id),
    { successMessage: t('ordenes_carga.messages.autorizada'), onSuccess: invalidarTodo }
  );

  const eliminarMutation = useApiMutation(
    (id: number) => ordenesCargaCombustibleService.eliminar(id),
    {
      successMessage: t('ordenes_carga.messages.eliminada'),
      onSuccess: () => { invalidarTodo(); setEliminarOrden(null); },
    }
  );

  const emitirMutation = useApiMutation(
    ({ id, datos }: { id: number; datos: OrdenCargaCombustibleEmitirRequest }) =>
      ordenesCargaCombustibleService.emitir(id, datos),
    {
      successMessage: t('ordenes_carga.messages.emitida_desde_solicitud'),
      onSuccess: () => { invalidarTodo(); setEmitirOrden(null); },
    }
  );

  const cargarMutation = useApiMutation(
    ({ ordenId, destinoId, cantidad, rut, nombre, foto, horometro, nivel, fotoNivelArchivo }: { ordenId: number; destinoId: number; cantidad: number; rut: string; nombre: string; foto: File; horometro?: number; nivel?: number; fotoNivelArchivo?: File }) =>
      ordenesCargaCombustibleService.cargar(ordenId, destinoId, {
        cantidadCargada: cantidad,
        rutCargador: rut,
        nombreCargador: nombre,
        fotoGuiaDespacho: foto,
        horometroDeclarado: horometro,
        nivelDeclarado: nivel,
        fotoEvidenciaNivel: fotoNivelArchivo,
      }),
    {
      successMessage: t('ordenes_carga.messages.cargada'),
      onSuccess: () => { invalidarTodo(); cerrarModalCargar(); },
    }
  );

  const aprobarMutation = useApiMutation(
    ({ ordenId, destinoId }: { ordenId: number; destinoId: number }) =>
      ordenesCargaCombustibleService.aprobarDestino(ordenId, destinoId),
    { successMessage: t('ordenes_carga.messages.aprobada'), onSuccess: invalidarTodo }
  );

  const enviarCorreoMutation = useApiMutation(
    (id: number) => ordenesCargaCombustibleService.enviarPorCorreo(id),
    { successMessage: t('ordenes_carga.messages.enviada_correo'), onSuccess: invalidarTodo }
  );

  const rechazarSolicitudMutation = useApiMutation(
    ({ id, observaciones }: { id: number; observaciones: string }) =>
      ordenesCargaCombustibleService.rechazarSolicitud(id, observaciones),
    {
      successMessage: t('ordenes_carga.messages.rechazada'),
      onSuccess: () => { invalidarTodo(); cerrarModalRechazar(); },
    }
  );

  const rechazarDestinoMutation = useApiMutation(
    ({ ordenId, destinoId, observaciones }: { ordenId: number; destinoId: number; observaciones: string }) =>
      ordenesCargaCombustibleService.rechazarDestino({ ordenId, destinoId, observacionesRevision: observaciones }),
    {
      successMessage: t('ordenes_carga.messages.rechazada'),
      onSuccess: () => { invalidarTodo(); cerrarModalRechazar(); },
    }
  );

  const regularizarMutation = useApiMutation(
    ({ ordenId, destinoId, cantidad, rut, nombre, horometro, nivel, foto, fotoNivelArchivo }: { ordenId: number; destinoId: number; cantidad?: number; rut?: string; nombre?: string; horometro?: number; nivel?: number; foto?: File; fotoNivelArchivo?: File }) =>
      ordenesCargaCombustibleService.regularizarCarga(ordenId, destinoId, {
        cantidadCargada: cantidad,
        rutCargador: rut,
        nombreCargador: nombre,
        horometroDeclarado: horometro,
        nivelDeclarado: nivel,
        fotoGuiaDespacho: foto,
        fotoEvidenciaNivel: fotoNivelArchivo,
      }),
    {
      successMessage: t('ordenes_carga.messages.regularizada'),
      onSuccess: (orden: OrdenCargaCombustible) => {
        invalidarTodo();
        // Refresca el modal de detalle (si está abierto) con la orden corregida.
        setDetalleOrden((prev) => (prev && prev.id === orden.id ? orden : prev));
        cerrarModalRegularizar();
      },
    }
  );

  const columnaDestinos = {
    key: 'destinos',
    title: t('ordenes_carga.table.vehiculo_maquina'),
    render: (_: any, item: OrdenCargaCombustible) => (
      <div className="space-y-1">
        {item.destinos.map((d) => (
          <div key={d.id} className="flex items-center gap-2 text-sm">
            <span>{textoDestino(d)}</span>
            <span className="text-gray-500">{textoCantidad(d, item.unidad)}</span>
            <Badge variant={badgeVariantPorEstadoDestino[d.estado]}>{t(`ordenes_carga.estado_destino.${d.estado}`)}</Badge>
          </div>
        ))}
      </div>
    ),
  };

  const columnsGenerales = useMemo(() => [
    { key: 'numero_orden', title: t('ordenes_carga.table.numero_orden'), render: (v: string | null) => v || '-' },
    { key: 'fecha_emision', title: t('ordenes_carga.table.fecha') },
    { key: 'proveedor', title: t('ordenes_carga.table.proveedor'), render: (v: any) => v?.nombre || '-' },
    { key: 'operador', title: t('ordenes_carga.table.usuario_autorizado'), render: (v: any) => v?.descripcion || '-' },
    columnaDestinos,
    {
      key: 'estado',
      title: t('ordenes_carga.table.estado'),
      render: (v: EstadoOrdenCarga, item: OrdenCargaCombustible) => (
        estadoHeaderEsRedundante(item) ? null : <Badge variant={badgeVariantPorEstado[v]}>{t(`ordenes_carga.estado.${v}`)}</Badge>
      ),
    },
    {
      key: 'id',
      title: '',
      render: (_: number, item: OrdenCargaCombustible) => (
        <div className="flex space-x-2">
          <Button size="sm" variant="secondary" onClick={() => setDetalleOrden(item)}>{t('ordenes_carga.actions.ver')}</Button>
          {item.estado === 'SOLICITADA' && canAprobar && (
            <Button
              size="sm"
              loading={autorizarMutation.isLoading && autorizarMutation.variables === item.id}
              onClick={() => autorizarMutation.mutate(item.id)}
            >
              {t('ordenes_carga.actions.autorizar')}
            </Button>
          )}
          {item.estado === 'AUTORIZADA' && canCrear && (
            <Button size="sm" onClick={() => setEmitirOrden(item)}>{t('ordenes_carga.actions.emitir')}</Button>
          )}
          {(item.estado === 'SOLICITADA' || item.estado === 'AUTORIZADA') && canAprobar && (
            <Button size="sm" variant="danger" onClick={() => setRechazarTarget({ tipo: 'solicitud', orden: item })}>{t('ordenes_carga.actions.rechazar')}</Button>
          )}
          {item.numero_orden && (
            <Button size="sm" variant="secondary" onClick={() => handleDescargarPdf(item)}>{t('ordenes_carga.actions.descargar_pdf')}</Button>
          )}
          {item.numero_orden && canCrear && (
            <Button
              size="sm"
              variant="secondary"
              disabled={!item.proveedor?.email}
              title={!item.proveedor?.email ? t('ordenes_carga.messages.proveedor_sin_correo') : undefined}
              loading={enviarCorreoMutation.isLoading && enviarCorreoMutation.variables === item.id}
              onClick={() => enviarCorreoMutation.mutate(item.id)}
            >
              {item.enviado_correo_en ? t('ordenes_carga.actions.reenviar_correo') : t('ordenes_carga.actions.enviar_correo')}
            </Button>
          )}
          {item.estado === 'PENDIENTE' && canEliminar && (
            <Button size="sm" variant="danger" onClick={() => setEliminarOrden(item)}>{t('ordenes_carga.actions.eliminar')}</Button>
          )}
        </div>
      ),
    },
  ], [t, canCrear, canAprobar, canEliminar, enviarCorreoMutation, autorizarMutation]);

  const columnsMias = useMemo(() => [
    { key: 'numero_orden', title: t('ordenes_carga.table.numero_orden'), render: (v: string | null) => v || '-' },
    { key: 'fecha_emision', title: t('ordenes_carga.table.fecha') },
    columnaDestinos,
    {
      key: 'estado',
      title: t('ordenes_carga.table.estado'),
      render: (v: EstadoOrdenCarga, item: OrdenCargaCombustible) => (
        estadoHeaderEsRedundante(item) ? null : <Badge variant={badgeVariantPorEstado[v]}>{t(`ordenes_carga.estado.${v}`)}</Badge>
      ),
    },
    {
      key: 'id',
      title: '',
      render: (_: number, item: OrdenCargaCombustible) => (
        <div className="flex flex-col space-y-1 items-start">
          <Button size="sm" variant="secondary" onClick={() => setDetalleOrden(item)}>{t('ordenes_carga.actions.ver')}</Button>
          {item.estado === 'PENDIENTE' && item.destinos.filter((d) => d.estado === 'PENDIENTE').map((d) => (
            <Button key={d.id} size="sm" onClick={() => setCargarTarget({ orden: item, destino: d })}>
              {t('ordenes_carga.actions.cargar')} ({textoDestino(d)})
            </Button>
          ))}
        </div>
      ),
    },
  ], [t]);

  const columnsRevisar = useMemo(() => [
    { key: 'numero_orden', title: t('ordenes_carga.table.numero_orden'), render: (_: any, item: DestinoPorRevisar) => item.orden.numero_orden || '-' },
    { key: 'operador', title: t('ordenes_carga.table.operador'), render: (_: any, item: DestinoPorRevisar) => item.orden.operador?.descripcion || '-' },
    { key: 'destino', title: t('ordenes_carga.table.vehiculo_maquina'), render: (_: any, item: DestinoPorRevisar) => textoDestino(item.destino) },
    {
      key: 'cantidad_cargada',
      title: t('ordenes_carga.table.cantidad_cargada'),
      render: (_: any, item: DestinoPorRevisar) => (item.destino.cantidad_cargada != null ? `${item.destino.cantidad_cargada} ${item.orden.unidad}` : '-'),
    },
    {
      key: 'nivel_horometro',
      title: `${t('ordenes_carga.cargar.nivel_declarado')} / ${t('ordenes_carga.cargar.horometro_declarado')}`,
      render: (_: any, item: DestinoPorRevisar) => (
        <span className="text-sm text-gray-600">
          {item.destino.nivel_declarado != null ? `${item.destino.nivel_declarado}%` : '-'}
          {' / '}
          {item.destino.horometro_declarado ?? '-'}
        </span>
      ),
    },
    {
      key: 'ruta_foto_guia_despacho',
      title: t('ordenes_carga.table.foto'),
      render: (_: any, item: DestinoPorRevisar) => (
        <div className="flex gap-2">
          {item.destino.ruta_foto_guia_despacho && (
            <FotoGuiaDespachoThumb ordenId={item.orden.id} destinoId={item.destino.id} />
          )}
          {item.destino.ruta_foto_evidencia_nivel && (
            <FotoEvidenciaNivelThumb ordenId={item.orden.id} destinoId={item.destino.id} />
          )}
          {!item.destino.ruta_foto_guia_despacho && !item.destino.ruta_foto_evidencia_nivel && '-'}
        </div>
      ),
    },
    {
      key: 'id',
      title: '',
      render: (_: any, item: DestinoPorRevisar) => (
        <div className="flex space-x-2">
          <Button size="sm" onClick={() => aprobarMutation.mutate({ ordenId: item.orden.id, destinoId: item.destino.id })}>{t('ordenes_carga.actions.aprobar')}</Button>
          <Button size="sm" variant="danger" onClick={() => setRechazarTarget({ tipo: 'destino', orden: item.orden, destino: item.destino })}>{t('ordenes_carga.actions.rechazar')}</Button>
        </div>
      ),
    },
  ], [t, aprobarMutation]);

  if (!cargandoConfig && !habilitado) {
    return <ErrorState message={t('ordenes_carga.module_disabled')} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('ordenes_carga.title')}</h1>
          <p className="text-gray-600 mt-1">{t('ordenes_carga.subtitle')}</p>
        </div>
        {canCrear && (
          <Button onClick={() => setCrearModalOpen(true)}>{t('ordenes_carga.actions.nueva')}</Button>
        )}
      </div>

      <div className="flex space-x-2 border-b border-gray-200">
        {tabs.map(tab => (
          <button
            key={tab}
            onClick={() => { setTabActiva(tab); setPage(1); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tabActiva === tab ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            {t(`ordenes_carga.tabs.${tab.replace('-', '_')}`)}
          </button>
        ))}
      </div>

      {tabActiva === 'general' && (
        <>
          <Card>
            <Card.Content>
              <Select
                label={t('ordenes_carga.filters.estado')}
                value={estadoFiltro}
                onChange={(e) => { setEstadoFiltro(e.target.value); setPage(1); }}
                options={[
                  { value: '', label: t('ordenes_carga.filters.todos') },
                  { value: 'SOLICITADA', label: t('ordenes_carga.estado.SOLICITADA') },
                  { value: 'AUTORIZADA', label: t('ordenes_carga.estado.AUTORIZADA') },
                  { value: 'PENDIENTE', label: t('ordenes_carga.estado.PENDIENTE') },
                  { value: 'APROBADA', label: t('ordenes_carga.estado.APROBADA') },
                  { value: 'RECHAZADA', label: t('ordenes_carga.estado.RECHAZADA') },
                ]}
              />
            </Card.Content>
          </Card>
          <Card padding="none">
            <DataTable
              columns={columnsGenerales}
              data={dataGeneral?.items || []}
              isLoading={loadingGeneral}
              emptyMessage={t('ordenes_carga.no_data')}
              pagination={dataGeneral ? {
                currentPage: dataGeneral.page,
                totalPages: dataGeneral.pages,
                totalItems: dataGeneral.total,
                itemsPerPage: dataGeneral.per_page,
                onPageChange: setPage,
              } : undefined}
            />
          </Card>
        </>
      )}

      {tabActiva === 'mis-ordenes' && (
        <>
          <div className="flex justify-end">
            <Button onClick={() => setSolicitarModalOpen(true)}>{t('ordenes_carga.actions.solicitar')}</Button>
          </div>
          <Card padding="none">
            <DataTable
              columns={columnsMias}
              data={dataMias?.items || []}
              isLoading={loadingMias}
              emptyMessage={t('ordenes_carga.no_pendientes')}
              pagination={dataMias ? {
                currentPage: dataMias.page,
                totalPages: dataMias.pages,
                totalItems: dataMias.total,
                itemsPerPage: dataMias.per_page,
                onPageChange: setPage,
              } : undefined}
            />
          </Card>
        </>
      )}

      {tabActiva === 'revisar' && (
        <Card padding="none">
          <DataTable
            columns={columnsRevisar}
            data={dataRevisar || []}
            isLoading={loadingRevisar}
            emptyMessage={t('ordenes_carga.no_data')}
          />
        </Card>
      )}

      <FormularioOrdenCarga
        isOpen={crearModalOpen}
        onClose={() => setCrearModalOpen(false)}
        onSubmit={(datos) => crearMutation.mutate(datos)}
        isLoading={crearMutation.isLoading}
      />

      <FormularioSolicitudCarga
        isOpen={solicitarModalOpen}
        onClose={() => setSolicitarModalOpen(false)}
        onSubmit={(datos) => solicitarMutation.mutate(datos)}
        isLoading={solicitarMutation.isLoading}
      />

      <FormularioEmitirOrden
        isOpen={!!emitirOrden}
        orden={emitirOrden}
        onClose={() => setEmitirOrden(null)}
        onSubmit={(datos) => emitirOrden && emitirMutation.mutate({ id: emitirOrden.id, datos })}
        isLoading={emitirMutation.isLoading}
      />

      <Modal isOpen={!!cargarTarget} onClose={cerrarModalCargar} title={t('ordenes_carga.cargar.title')}>
        {cargarTarget && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {t('ordenes_carga.cargar.orden_info', {
                numero: cargarTarget.orden.numero_orden,
                cantidad: cargarTarget.destino.cantidad_autorizada,
                unidad: cargarTarget.orden.unidad,
              })}
            </p>
            <Input
              type="number"
              step="0.01"
              min="0"
              label={t('ordenes_carga.cargar.cantidad_cargada')}
              value={cantidadCargada}
              onChange={(e) => setCantidadCargada(e.target.value)}
              required
            />
            <Input
              label={t('ordenes_carga.cargar.nombre_cargador')}
              placeholder={t('ordenes_carga.cargar.nombre_cargador_placeholder')}
              value={nombreCargador}
              onChange={(e) => setNombreCargador(e.target.value)}
              required
            />
            <Input
              label={t('ordenes_carga.cargar.rut_cargador')}
              placeholder={t('ordenes_carga.cargar.rut_cargador_placeholder')}
              value={rutCargador}
              onChange={(e) => setRutCargador(e.target.value)}
              required
            />
            <div className="grid grid-cols-2 gap-3">
              <Input
                type="number"
                step="0.01"
                min="0"
                label={t('ordenes_carga.cargar.horometro_declarado')}
                value={horometroDeclarado}
                onChange={(e) => setHorometroDeclarado(e.target.value)}
              />
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                label={t('ordenes_carga.cargar.nivel_declarado')}
                value={nivelDeclarado}
                onChange={(e) => setNivelDeclarado(e.target.value)}
              />
            </div>
            <div>
              <Button variant="secondary" type="button" onClick={fotoGuia.capturarFoto}>
                {fotoGuia.fotoFile ? t('ordenes_carga.cargar.retomar_foto') : t('ordenes_carga.cargar.tomar_foto')}
              </Button>
              {fotoGuia.fotoFile && (
                <img
                  src={URL.createObjectURL(fotoGuia.fotoFile)}
                  alt="guía de despacho"
                  className="mt-3 w-full max-h-64 object-contain rounded border border-gray-200"
                />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('ordenes_carga.cargar.foto_nivel_label')}
              </label>
              <Button variant="secondary" type="button" onClick={fotoNivel.capturarFoto}>
                {fotoNivel.fotoFile ? t('ordenes_carga.cargar.retomar_foto') : t('ordenes_carga.cargar.tomar_foto_nivel')}
              </Button>
              {fotoNivel.fotoFile && (
                <img
                  src={URL.createObjectURL(fotoNivel.fotoFile)}
                  alt="horómetro y nivel"
                  className="mt-3 w-full max-h-64 object-contain rounded border border-gray-200"
                />
              )}
            </div>
            <div className="flex justify-end space-x-3 pt-2">
              <Button variant="secondary" onClick={cerrarModalCargar}>{t('common.cancel')}</Button>
              <Button
                disabled={!fotoGuia.listo || !cantidadCargada || Number(cantidadCargada) <= 0 || rutCargador.trim() === '' || nombreCargador.trim() === ''}
                loading={cargarMutation.isLoading}
                onClick={() => cargarTarget && fotoGuia.fotoFile && cargarMutation.mutate({
                  ordenId: cargarTarget.orden.id,
                  destinoId: cargarTarget.destino.id,
                  cantidad: Number(cantidadCargada),
                  rut: rutCargador.trim(),
                  nombre: nombreCargador.trim(),
                  foto: fotoGuia.fotoFile,
                  horometro: horometroDeclarado ? Number(horometroDeclarado) : undefined,
                  nivel: nivelDeclarado ? Number(nivelDeclarado) : undefined,
                  fotoNivelArchivo: fotoNivel.fotoFile || undefined,
                })}
              >
                {t('ordenes_carga.cargar.confirmar')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal isOpen={!!rechazarTarget} onClose={cerrarModalRechazar} title={t('ordenes_carga.rechazar.title')}>
        {rechazarTarget && (
          <div className="space-y-4">
            <Textarea
              label={t('ordenes_carga.rechazar.motivo')}
              value={observacionesRechazo}
              onChange={(e) => setObservacionesRechazo(e.target.value)}
              rows={3}
              required
            />
            <div className="flex justify-end space-x-3 pt-2">
              <Button variant="secondary" onClick={cerrarModalRechazar}>{t('common.cancel')}</Button>
              <Button
                variant="danger"
                disabled={observacionesRechazo.trim().length < 3}
                loading={rechazarSolicitudMutation.isLoading || rechazarDestinoMutation.isLoading}
                onClick={() => {
                  if (!rechazarTarget) return;
                  if (rechazarTarget.tipo === 'solicitud') {
                    rechazarSolicitudMutation.mutate({ id: rechazarTarget.orden.id, observaciones: observacionesRechazo });
                  } else {
                    rechazarDestinoMutation.mutate({
                      ordenId: rechazarTarget.orden.id,
                      destinoId: rechazarTarget.destino.id,
                      observaciones: observacionesRechazo,
                    });
                  }
                }}
              >
                {t('ordenes_carga.rechazar.confirmar')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal isOpen={!!eliminarOrden} onClose={() => setEliminarOrden(null)} title={t('ordenes_carga.eliminar.title')}>
        {eliminarOrden && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              {t('ordenes_carga.eliminar.confirmacion', { numero: eliminarOrden.numero_orden || '-' })}
            </p>
            <div className="flex justify-end space-x-3 pt-2">
              <Button variant="secondary" onClick={() => setEliminarOrden(null)}>{t('common.cancel')}</Button>
              <Button
                variant="danger"
                loading={eliminarMutation.isLoading}
                onClick={() => eliminarMutation.mutate(eliminarOrden.id)}
              >
                {t('ordenes_carga.eliminar.confirmar')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal isOpen={!!detalleOrden} onClose={() => setDetalleOrden(null)} title={t('ordenes_carga.detalle.title', { numero: detalleOrden?.numero_orden || '-' })}>
        {detalleOrden && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div><span className="text-gray-500">{t('ordenes_carga.table.fecha')}:</span> {detalleOrden.fecha_emision}</div>
              <div><span className="text-gray-500">{t('ordenes_carga.table.estado')}:</span> <Badge variant={badgeVariantPorEstado[detalleOrden.estado]}>{t(`ordenes_carga.estado.${detalleOrden.estado}`)}</Badge></div>
              <div><span className="text-gray-500">{t('ordenes_carga.table.proveedor')}:</span> {detalleOrden.proveedor?.nombre || '-'}</div>
              <div><span className="text-gray-500">{t('ordenes_carga.detalle.proveedor_email')}:</span> {detalleOrden.proveedor?.email || '-'}</div>
              <div><span className="text-gray-500">{t('ordenes_carga.table.usuario_autorizado')}:</span> {detalleOrden.operador?.descripcion || '-'}</div>
              <div><span className="text-gray-500">{t('ordenes_carga.form.tarjeta')}:</span> {detalleOrden.tarjeta ? (detalleOrden.tarjeta.compania ? `${detalleOrden.tarjeta.compania} - ${detalleOrden.tarjeta.numero}` : detalleOrden.tarjeta.numero) : '-'}</div>
              <div><span className="text-gray-500">{t('ordenes_carga.detalle.emisor')}:</span> {detalleOrden.emisor ? [detalleOrden.emisor.nombre, detalleOrden.emisor.cargo].filter(Boolean).join(' — ') : '-'}</div>
              {detalleOrden.enviado_correo_en && (
                <div className="col-span-2"><span className="text-gray-500">{t('ordenes_carga.detalle.enviado_correo')}:</span> {detalleOrden.enviado_correo_en}</div>
              )}
              {detalleOrden.observaciones_solicitud && (
                <div className="col-span-2"><span className="text-gray-500">{t('ordenes_carga.detalle.observaciones_solicitud')}:</span> {detalleOrden.observaciones_solicitud}</div>
              )}
            </div>

            <div className="space-y-3">
              {detalleOrden.destinos.map((d) => (
                <div key={d.id} className="border border-gray-200 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{textoDestino(d)}</span>
                    <Badge variant={badgeVariantPorEstadoDestino[d.estado]}>{t(`ordenes_carga.estado_destino.${d.estado}`)}</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <div><span className="text-gray-500">{t('ordenes_carga.detalle.cantidad_solicitada')}:</span> {d.cantidad_solicitada ?? '-'}</div>
                    <div><span className="text-gray-500">{t('ordenes_carga.form.cantidad_autorizada')}:</span> {d.cantidad_autorizada ?? '-'}</div>
                    <div><span className="text-gray-500">{t('ordenes_carga.table.cantidad_cargada')}:</span> {d.cantidad_cargada ?? '-'}</div>
                    <div><span className="text-gray-500">{t('ordenes_carga.cargar.nombre_cargador')}:</span> {d.nombre_cargador || '-'}</div>
                    <div><span className="text-gray-500">{t('ordenes_carga.cargar.rut_cargador')}:</span> {d.rut_cargador || '-'}</div>
                    <div><span className="text-gray-500">{t('ordenes_carga.cargar.horometro_declarado')}:</span> {d.horometro_declarado ?? '-'}</div>
                    <div><span className="text-gray-500">{t('ordenes_carga.cargar.nivel_declarado')}:</span> {d.nivel_declarado != null ? `${d.nivel_declarado}%` : '-'}</div>
                    <div><span className="text-gray-500">{t('ordenes_carga.detalle.fecha_carga')}:</span> {d.fecha_carga || '-'}</div>
                    {d.fecha_revision && (
                      <div><span className="text-gray-500">{t('ordenes_carga.detalle.fecha_revision')}:</span> {d.fecha_revision}</div>
                    )}
                    {d.observaciones_revision && (
                      <div className="col-span-2"><span className="text-gray-500">{t('ordenes_carga.detalle.observaciones_revision')}:</span> {d.observaciones_revision}</div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {d.ruta_foto_guia_despacho && (
                      <FotoGuiaDespachoThumb ordenId={detalleOrden.id} destinoId={d.id} />
                    )}
                    {d.ruta_foto_evidencia_nivel && (
                      <FotoEvidenciaNivelThumb ordenId={detalleOrden.id} destinoId={d.id} />
                    )}
                  </div>
                  {canAprobar && (d.estado === 'CARGADA' || d.estado === 'APROBADA' || d.estado === 'RECHAZADA') && (
                    <div className="pt-1">
                      <Button size="sm" variant="secondary" onClick={() => abrirRegularizar(detalleOrden, d)}>
                        {t('ordenes_carga.actions.regularizar')}
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end space-x-3 pt-2">
              <Button variant="secondary" onClick={() => setDetalleOrden(null)}>{t('common.close')}</Button>
              {detalleOrden.numero_orden && (
                <Button onClick={() => handleDescargarPdf(detalleOrden)}>{t('ordenes_carga.actions.descargar_pdf')}</Button>
              )}
            </div>
          </div>
        )}
      </Modal>

      <Modal isOpen={!!regularizarTarget} onClose={cerrarModalRegularizar} title={t('ordenes_carga.regularizar.title')}>
        {regularizarTarget && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {t('ordenes_carga.regularizar.descripcion', {
                numero: regularizarTarget.orden.numero_orden || '-',
                destino: textoDestino(regularizarTarget.destino),
              })}
            </p>
            <Input
              type="number"
              step="0.01"
              min="0"
              label={t('ordenes_carga.cargar.cantidad_cargada')}
              value={regCantidad}
              onChange={(e) => setRegCantidad(e.target.value)}
              required
            />
            <Input
              label={t('ordenes_carga.cargar.nombre_cargador')}
              placeholder={t('ordenes_carga.cargar.nombre_cargador_placeholder')}
              value={regNombre}
              onChange={(e) => setRegNombre(e.target.value)}
            />
            <Input
              label={t('ordenes_carga.cargar.rut_cargador')}
              placeholder={t('ordenes_carga.cargar.rut_cargador_placeholder')}
              value={regRut}
              onChange={(e) => setRegRut(e.target.value)}
            />
            <div className="grid grid-cols-2 gap-3">
              <Input
                type="number"
                step="0.01"
                min="0"
                label={t('ordenes_carga.cargar.horometro_declarado')}
                value={regHorometro}
                onChange={(e) => setRegHorometro(e.target.value)}
              />
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                label={t('ordenes_carga.cargar.nivel_declarado')}
                value={regNivel}
                onChange={(e) => setRegNivel(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('ordenes_carga.regularizar.foto_guia_label')}
              </label>
              <Button variant="secondary" type="button" onClick={fotoGuiaReg.capturarFoto}>
                {fotoGuiaReg.fotoFile ? t('ordenes_carga.cargar.retomar_foto') : t('ordenes_carga.cargar.tomar_foto')}
              </Button>
              {fotoGuiaReg.fotoFile && (
                <img
                  src={URL.createObjectURL(fotoGuiaReg.fotoFile)}
                  alt="guía de despacho"
                  className="mt-3 w-full max-h-64 object-contain rounded border border-gray-200"
                />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('ordenes_carga.cargar.foto_nivel_label')}
              </label>
              <Button variant="secondary" type="button" onClick={fotoNivelReg.capturarFoto}>
                {fotoNivelReg.fotoFile ? t('ordenes_carga.cargar.retomar_foto') : t('ordenes_carga.cargar.tomar_foto_nivel')}
              </Button>
              {fotoNivelReg.fotoFile && (
                <img
                  src={URL.createObjectURL(fotoNivelReg.fotoFile)}
                  alt="horómetro y nivel"
                  className="mt-3 w-full max-h-64 object-contain rounded border border-gray-200"
                />
              )}
            </div>
            <div className="flex justify-end space-x-3 pt-2">
              <Button variant="secondary" onClick={cerrarModalRegularizar}>{t('common.cancel')}</Button>
              <Button
                disabled={!regCantidad || Number(regCantidad) <= 0}
                loading={regularizarMutation.isLoading}
                onClick={() => regularizarTarget && regularizarMutation.mutate({
                  ordenId: regularizarTarget.orden.id,
                  destinoId: regularizarTarget.destino.id,
                  cantidad: regCantidad ? Number(regCantidad) : undefined,
                  rut: regRut.trim() || undefined,
                  nombre: regNombre.trim() || undefined,
                  horometro: regHorometro ? Number(regHorometro) : undefined,
                  nivel: regNivel ? Number(regNivel) : undefined,
                  foto: fotoGuiaReg.fotoFile || undefined,
                  fotoNivelArchivo: fotoNivelReg.fotoFile || undefined,
                })}
              >
                {t('ordenes_carga.regularizar.confirmar')}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OrdenesCargaCombustibleView;
